import { useEffect, useState } from 'react';
import { Segmented } from 'antd';
import {
  CommentOutlined,
  CopyOutlined,
  HistoryOutlined,
  LockOutlined,
  MessageOutlined,
  RobotOutlined,
  SearchOutlined,
} from '@ant-design/icons';

import { Bento, Tile, TileBody, TileHeader } from '../../../../components/shared';
import { Badge, Button, Empty, Input, Select, Skeleton } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { ngayGioVN, soVN } from '../../../../lib/dinh-dang';
import type { ApiError } from '../../../../lib/errors';
import { useToast } from '../../../../lib/toast';
import { DaiLoi } from '../chung/VungDuLieu';
import { LoiVan } from '../chung/LoiVan';
import { KhoiSoLieuDaLuu } from '../hoi/KhoiSoLieuDaLuu';
import { tenVaiTuDanhSach, useVaiTroToChuc } from '../../hooks/useChung';
import { useChiTietHoiThoai, useHoiThoaiNhatKy } from '../../hooks/useVanHanh';
import { chuoiTrongPayload, laKhachNhung } from '../../nghiep-vu/van-hanh';
import type { HoiThoaiMessage, HoiThoaiNhatKy, LocNhatKyHoiThoai } from '../../types';

type Loai = NonNullable<LocNhatKyHoiThoai['loai']>;

const TRE_TIM_MS = 300;
const KY_NGAY = [7, 30, 90] as const;
const KY_MAC_DINH = 30;

function useTre<T>(giaTri: T, ms: number): T {
  const [tre, setTre] = useState(giaTri);
  useEffect(() => {
    const h = setTimeout(() => setTre(giaTri), ms);
    return () => clearTimeout(h);
  }, [giaTri, ms]);
  return tre;
}

/** Tên vai cho người đọc; khách khung nhúng (`nhung:<phiên>`) hiện "Khách (khung nhúng)", không hiện mã phiên. */
function useTenVai(): (vai: string) => string {
  const { t } = useT();
  const ds = useVaiTroToChuc();
  return (vai) => (laKhachNhung(vai) ? t('troLyAiVanHanhNhatKy.khachNhung') : tenVaiTuDanhSach(vai, ds.data));
}

function DongDoan({ d, chon, onChon }: { d: HoiThoaiNhatKy; chon: boolean; onChon: () => void }) {
  const { t, locale } = useT();
  const ten = useTenVai();
  return (
    <li>
      <button
        type="button"
        onClick={onChon}
        aria-current={chon ? 'true' : undefined}
        className={cn(
          'w-full rounded-lg px-3 py-2.5 text-start transition-colors',
          chon ? 'bg-primary-50' : 'hover:bg-subtle',
        )}
      >
        <div className="flex items-start gap-2">
          <span aria-hidden className={cn('mt-0.5 shrink-0', d.loai === 'chatbot' ? 'text-violet-600' : 'text-sky-600')}>
            {d.loai === 'chatbot' ? <RobotOutlined /> : <MessageOutlined />}
          </span>
          <div className="min-w-0 flex-1">
            <p className={cn('line-clamp-2 text-sm', chon ? 'font-medium text-primary-700' : 'text-content-primary')}>
              {d.title || t('troLyAiVanHanhNhatKy.doanChuaDatTen')}
            </p>
            <p className="mt-0.5 truncate text-xs text-content-muted">
              {d.nhan} · {ten(d.vai)}
            </p>
          </div>
          <div className="shrink-0 text-end">
            <p className="text-xs tabular-nums text-content-muted">{ngayGioVN(d.updatedAt, locale)}</p>
            <p className="text-xs tabular-nums text-content-secondary">
              {t('troLyAiVanHanhNhatKy.soLuot', { n: soVN(d.luot, locale) })}
            </p>
          </div>
        </div>
      </button>
    </li>
  );
}

/**
 * Một tin nhắn. Liên kết "Xem chuỗi xử lý" của native mở Nhật ký thao tác lọc theo `traceId` và quãng thời gian của
 * cuộc trò chuyện.
 *
 * TODO(ADR-0086): màn Nhật ký thao tác của platform (`/nhat-ky/thao-tac`) chưa đọc bộ lọc từ URL và audit-service chưa
 * tìm theo mã tra cứu — nên tạm hiện `traceId` kèm nút chép thay cho đường dẫn. Khi màn ấy nhận `?tim=`/`from`/`to`
 * thì đổi lại thành liên kết như native.
 */
function LuotTin({ m }: { m: HoiThoaiMessage }) {
  const { t } = useT();
  const toast = useToast();
  if (m.author === 'user') {
    return (
      <div className="flex justify-end">
        <p className="max-w-[80%] whitespace-pre-wrap rounded-lg bg-primary-50 px-4 py-2.5 text-sm text-primary-700">
          {m.text}
        </p>
      </div>
    );
  }
  const sql = chuoiTrongPayload(m.payload, 'sql');
  const traceId = chuoiTrongPayload(m.payload, 'traceId');
  const chep = () => {
    if (!traceId) return;
    void navigator.clipboard?.writeText(traceId).then(
      () => toast.success(t('troLyAiVanHanhNhatKy.daChepMa')),
      () => undefined,
    );
  };
  return (
    <div className="space-y-2">
      <LoiVan text={m.text} className="text-sm leading-relaxed text-content-primary" />
      <KhoiSoLieuDaLuu payload={m.payload} />
      {sql ? (
        <details className="rounded-lg bg-subtle px-3 py-2">
          <summary className="cursor-pointer select-none text-xs font-medium text-content-secondary">
            {t('troLyAiVanHanhNhatKy.xemCauLenh')}
          </summary>
          <pre className="mt-2 overflow-x-auto font-mono text-xs leading-relaxed text-content-primary">{sql}</pre>
        </details>
      ) : null}
      {traceId ? (
        <p className="flex flex-wrap items-center gap-1.5 text-xs text-content-muted">
          <HistoryOutlined aria-hidden />
          <span>{t('troLyAiVanHanhNhatKy.maTraCuu')}</span>
          <code className="rounded bg-subtle px-1 font-mono text-xs text-content-secondary">{traceId}</code>
          <Button
            size="sm"
            variant="ghost"
            icon={<CopyOutlined />}
            title={t('troLyAiVanHanhNhatKy.chepMa')}
            aria-label={t('troLyAiVanHanhNhatKy.chepMa')}
            onClick={chep}
          />
        </p>
      ) : null}
    </div>
  );
}

function NoiDungDoan({ cvId, dong }: { cvId: string | null; dong?: HoiThoaiNhatKy }) {
  const { t, locale } = useT();
  const ten = useTenVai();
  const q = useChiTietHoiThoai(cvId);

  if (!cvId) {
    return (
      <Empty
        icon={CommentOutlined}
        title={t('troLyAiVanHanhNhatKy.chonDoan')}
        description={t('troLyAiVanHanhNhatKy.chonDoanMo')}
      />
    );
  }
  return (
    <>
      {dong ? (
        <TileHeader
          icon={dong.loai === 'chatbot' ? <RobotOutlined /> : <MessageOutlined />}
          tone={dong.loai === 'chatbot' ? 'violet' : 'sky'}
          title={dong.title || t('troLyAiVanHanhNhatKy.doanChuaDatTen')}
          mo={t('troLyAiVanHanhNhatKy.moDoan', {
            nhan: dong.nhan,
            vai: ten(dong.vai),
            luc: ngayGioVN(dong.createdAt, locale),
          })}
          action={
            <Badge variant="neutral">
              {dong.loai === 'chatbot' ? t('troLyAiVanHanhNhatKy.loaiChatbot') : t('troLyAiVanHanhNhatKy.loaiTroLy')}
            </Badge>
          }
        />
      ) : (
        // Mở từ đường dẫn `?cv=`: cuộc trò chuyện có thể nằm ngoài bộ lọc của danh sách, nên tiêu đề lấy từ nội dung.
        <TileHeader
          icon={<CommentOutlined />}
          tone="violet"
          title={q.data?.title || t('troLyAiVanHanhNhatKy.doanChuaDatTen')}
          mo={t('troLyAiVanHanhNhatKy.moTuNhatKy')}
        />
      )}
      <TileBody className="max-h-[70vh] space-y-4 overflow-y-auto">
        {q.isPending ? (
          <div className="space-y-3" aria-busy="true">
            <Skeleton className="ms-auto h-10 w-2/3" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : q.error ? (
          <DaiLoi loi={q.error} onThuLai={() => void q.refetch()} />
        ) : (
          (q.data?.messages ?? []).map((m) => <LuotTin key={m.id} m={m} />)
        )}
      </TileBody>
    </>
  );
}

/**
 * Tab "Lịch sử trò chuyện": danh sách bên trái, nội dung đoạn bên phải. Hai cột chứ không hộp thoại: việc ở đây là đọc
 * LẦN LƯỢT nhiều đoạn để tìm một mẫu hình — hộp thoại bắt đóng mở cho mỗi đoạn và che danh sách đúng lúc cần so.
 */
export function LichSuTroChuyen({ cvMoDau }: { cvMoDau?: string | null }) {
  const { t } = useT();
  const [tim, setTim] = useState('');
  const [loai, setLoai] = useState<Loai>('');
  const [ngay, setNgay] = useState<number>(KY_MAC_DINH);
  const [chon, setChon] = useState<string | null>(cvMoDau ?? null);
  const timTre = useTre(tim.trim(), TRE_TIM_MS);
  const q = useHoiThoaiNhatKy({ q: timTre, loai, ngay });
  const dangLoc = timTre !== '' || loai !== '';

  if ((q.error as ApiError | null)?.status === 403) {
    return (
      <Bento>
        <Tile>
          <Empty
            icon={LockOutlined}
            title={t('troLyAiVanHanhNhatKy.chiQuanTri')}
            description={t('troLyAiVanHanhNhatKy.chiQuanTriMo')}
          />
        </Tile>
      </Bento>
    );
  }

  const ds = q.data ?? [];

  return (
    <Bento>
      <Tile span="lg:col-span-5" className="flex flex-col overflow-hidden">
        <div className="space-y-2 border-b border-default px-3 py-3">
          <Input
            allowClear
            prefix={<SearchOutlined className="text-content-muted" />}
            placeholder={t('troLyAiVanHanhNhatKy.timTroChuyen')}
            aria-label={t('troLyAiVanHanhNhatKy.timTroChuyen')}
            value={tim}
            onChange={(e) => setTim(e.target.value)}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Segmented<Loai>
              size="small"
              aria-label={t('troLyAiVanHanhNhatKy.locLoai')}
              value={loai}
              onChange={setLoai}
              options={[
                { value: '', label: t('troLyAiVanHanhNhatKy.locTatCa') },
                { value: 'tro_ly', label: t('troLyAiVanHanhNhatKy.loaiTroLy') },
                { value: 'chatbot', label: t('troLyAiVanHanhNhatKy.loaiChatbot') },
              ]}
            />
            <Select<number>
              size="small"
              className="ms-auto w-32"
              aria-label={t('troLyAiVanHanhNhatKy.khoangNgay')}
              value={ngay}
              onChange={setNgay}
              options={KY_NGAY.map((n) => ({ value: n, label: t('troLyAiVanHanhNhatKy.trongNgay', { n }) }))}
            />
          </div>
        </div>
        {q.isPending ? (
          <div className="space-y-2 p-3" aria-busy="true">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : q.error ? (
          <DaiLoi loi={q.error} onThuLai={() => void q.refetch()} />
        ) : ds.length === 0 ? (
          dangLoc ? (
            // Rỗng vì bộ lọc: chỉ tiêu đề, không mời làm gì thêm.
            <Empty icon={CommentOutlined} title={t('troLyAiVanHanhNhatKy.rongTroChuyen')} />
          ) : (
            <Empty
              icon={CommentOutlined}
              title={t('troLyAiVanHanhNhatKy.rongTroChuyen')}
              description={t('troLyAiVanHanhNhatKy.rongTroChuyenKhongLoc')}
            />
          )
        ) : (
          <ul
            aria-label={t('troLyAiVanHanhNhatKy.danhSachDoan')}
            className={cn('max-h-[70vh] space-y-1 overflow-y-auto p-2', q.isFetching && 'opacity-60')}
          >
            {ds.map((d) => (
              <DongDoan key={d.id} d={d} chon={d.id === chon} onChon={() => setChon(d.id)} />
            ))}
          </ul>
        )}
      </Tile>
      <Tile span="lg:col-span-7" className="overflow-hidden">
        <NoiDungDoan cvId={chon} dong={ds.find((d) => d.id === chon)} />
      </Tile>
    </Bento>
  );
}
